import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search,
  Plus,
  CheckCircle2,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownLeft,
  PackageCheck,
  Truck,
  ClipboardCheck,
  RotateCcw,
  Loader2,
  Wrench,
  HelpCircle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { api } from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import FieldPageChrome from "../../components/field/FieldPageChrome";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { unwrapArray } from "../../lib/api-envelope";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { formatInstant } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { apiErrorText, runWriteSteps } from "../../lib/write-steps";
import type { RentalPeriodListRow } from "../../types/rental-period";
import { RentalInspectionPhotoField, newInspectionId } from "../../components/rentals/RentalInspectionPhotos";
import { inspectionPhotoExtras } from "../../lib/rental-inspections";

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Movement {
  id: string;
  type: "checkout" | "return";
  assetName: string;
  assetTag: string;
  customer: string;
  well: string;
  dateTime: string;
  /** The signed-in user's name, or null when it is not known. Never a literal. */
  tech: string | null;
  condition: "good" | "fair" | "damaged";
  /** Null when no photo count is recorded. Not 0 — absent is not zero. */
  photos: number | null;
}

/**
 * A rental asset as GET /surface-rentals/assets sends it, under the names the
 * check-out and check-in handlers below already use. `tag` is `asset_number`
 * and `yard_location` is `current_location`. The API sends no `current_order`
 * and no `days_out`, so neither is read.
 */
interface YardAsset {
  id: string;
  name: string;
  tag: string;
  status: string | null;
  category: string | null;
  yard_location: string | null;
  current_order?: { id: string; customer: string; well: string } | null;
}

/** GET /surface-rentals/orders: `ro.*` plus `customer_name`. `well_name`, not `well`. */
interface Order {
  id: string;
  customer: string;
  well?: string;
  order_number?: string;
  status?: string;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));

function toYardAsset(r: Record<string, unknown>): YardAsset {
  return {
    id: String(r.id ?? ""),
    name: String(r.name ?? "—"),
    tag: String(r.asset_number ?? ""),
    status: str(r.status),
    category: str(r.category),
    yard_location: str(r.current_location),
  };
}

function toOrder(r: Record<string, unknown>): Order {
  return {
    id: String(r.id ?? ""),
    customer: String(r.customer_name ?? ""),
    well: str(r.well_name) ?? undefined,
    order_number: str(r.order_number) ?? undefined,
    status: str(r.status) ?? undefined,
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Condition as a fill badge with dark ink: a fill, never an ink colour. */
const conditionConfig = {
  good: { fill: "bg-fi-signal-green", label: "Good" },
  fair: { fill: "bg-fi-signal-amber", label: "Fair" },
  damaged: { fill: "bg-fi-signal-red", label: "Damaged" },
};

const checklistItems = [
  "All mounting pins / locking pins secured",
  "Hydraulic fittings tight, no leaks",
  "Safety decals present and legible",
  "Tires / skid shoes in serviceable condition",
  "Lights and reflectors functional",
  "Fire extinguisher present and tagged",
  "Drip pans / containment installed",
  "All fluid levels checked",
];

type Skin = { label: string; fill: string; rail: string };

/**
 * The statuses the API writes to `rental_assets.status` (`available`, `rented`,
 * `reserved` since api#1091, `maintenance`, `retired`), on the Equipment
 * register's hues. This page used
 * to read `deployed` and `reserved`, which the API never writes, so its
 * Deployed and Reserved lists could only fill with rows this page wrote itself.
 *
 * The check-out used to write `deployed` on top of the `rented` the period POST
 * had just set (#815). Such a row is not "on rent" to the API, which counts
 * only `rented` for billing. Rows it left behind show here under Unrecognised
 * status with a slate dash, so they stay visible rather than vanishing, and
 * never borrow On Rent's label.
 */
const STATUS: Record<string, Skin> = {
  available: { label: "Available", fill: "bg-fi-signal-green", rail: "border-l-fi-green" },
  rented: { label: "On Rent", fill: "bg-fi-signal-blue", rail: "border-l-fi-blue" },
  // api#1091: on an upcoming order, not yet delivered. Blue as On Rent (both are
  // committed to an order); the label tells them apart. Not "available".
  reserved: { label: "Reserved", fill: "bg-fi-signal-blue", rail: "border-l-fi-blue" },
  maintenance: { label: "Maintenance", fill: "bg-fi-signal-red", rail: "border-l-fi-red" },
  retired: { label: "Retired", fill: "bg-fi-signal-slate", rail: "border-l-fi-steel" },
};
const UNKNOWN: Skin = { label: "—", fill: "bg-fi-signal-slate", rail: "border-l-transparent" };
const skinOf = (s: string | null) =>
  s != null && Object.prototype.hasOwnProperty.call(STATUS, s) ? STATUS[s] : UNKNOWN;

function StatusBadge({ status }: { status: string | null }) {
  const skin = skinOf(status);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

type YardTab = "available" | "rented" | "reserved" | "maintenance" | "other";
type ChecklistStatus = "pending" | "ok" | "issue";

const YARD_CHIPS: { key: YardTab; label: string; match: (a: YardAsset) => boolean }[] = [
  { key: "available", label: "Available", match: (a) => a.status === "available" },
  { key: "rented", label: "On Rent", match: (a) => a.status === "rented" },
  { key: "reserved", label: "Reserved", match: (a) => a.status === "reserved" },
  { key: "maintenance", label: "Maintenance", match: (a) => a.status === "maintenance" },
  { key: "other", label: "Unrecognised status", match: (a) => skinOf(a.status) === UNKNOWN },
];

const nativeSelect =
  "h-9 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * `surface="field"` is the rental tech's Yard tile (the /my-work field tree,
 * fe#815): the casing field header and back arrow, New Check-Out first, then
 * the same tabs, lists and dialogs — no office band and no stat strip. The
 * office tree passes nothing and renders exactly as before. Same queries and
 * writes either way; only the chrome differs.
 */
export default function RentalYard({ surface = "office" }: { surface?: "office" | "field" } = {}) {
  const isField = surface === "field";
  // A movement's stamp is read on the TENANT's clock (companies.time_zone).
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  /**
   * The name to record against a movement this session just made — the real
   * signed-in user, or null. It used to be the literal string "Current User",
   * which rendered as a person's name beside an avatar showing their initials
   * ("CU"), on a row that looks exactly like recorded history.
   */
  const currentUserName: string | null =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
    user?.name ||
    user?.email ||
    null;

  // Navigation
  const [activeTab, setActiveTab] = useState<"movements" | "pending" | "yard">("yard");
  const [yardSubTab, setYardSubTab] = useState<YardTab>("available");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");

  // Data state (orders kept as lazy-loaded state)
  const [orders, setOrders] = useState<Order[]>([]);
  const [localMovements, setLocalMovements] = useState<Movement[]>([]);

  // Loading / error
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Check-Out dialog
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checklistStatuses, setChecklistStatuses] = useState<ChecklistStatus[]>(
    new Array(checklistItems.length).fill("pending") as ChecklistStatus[]
  );
  const [checkoutAssetId, setCheckoutAssetId] = useState("");
  const [checkoutOrderId, setCheckoutOrderId] = useState("");
  const [checkoutCondition, setCheckoutCondition] = useState<"good" | "fair" | "damaged">("good");
  const [checkoutNotes, setCheckoutNotes] = useState("");
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  /**
   * Each dialog's inspection id is minted when the form starts, so photos can
   * be uploaded against it before the inspection is written (api#1066). A
   * fresh form gets a fresh id; the photo field is keyed on it and starts empty.
   */
  const [checkoutInspectionId, setCheckoutInspectionId] = useState(newInspectionId);
  const [checkoutPhotos, setCheckoutPhotos] = useState<string[]>([]);
  const [checkoutPhotosBusy, setCheckoutPhotosBusy] = useState(false);

  // Check-In dialog
  const [checkinDialogOpen, setCheckinDialogOpen] = useState(false);
  const [checkinAsset, setCheckinAsset] = useState<YardAsset | null>(null);
  const [checkinCondition, setCheckinCondition] = useState<"good" | "fair" | "damaged">("good");
  const [checkinNotes, setCheckinNotes] = useState("");
  const [checkinSubmitting, setCheckinSubmitting] = useState(false);
  const [checkinInspectionId, setCheckinInspectionId] = useState(newInspectionId);
  const [checkinPhotos, setCheckinPhotos] = useState<string[]>([]);
  const [checkinPhotosBusy, setCheckinPhotosBusy] = useState(false);

  // ─── Data fetching ────────────────────────────────────────────────────────

  /**
   * ⚠️ THIS DOES NOT FETCH MOVEMENT HISTORY, AND THERE IS NO ENDPOINT THAT DOES.
   *
   * `api.rentals.checkout.checklist()` is GET /surface-rentals/checkout/checklist,
   * which returns a hardcoded ten-item pre-delivery CHECKLIST -- "Visual
   * inspection complete", "Fluid levels checked" -- cast here to `Movement[]`.
   * It happens to yield [] rather than ten malformed rows only because the
   * envelope fails `Array.isArray`, so the bug has never been visible.
   *
   * Consequence: Movement History shows only rows this browser session just
   * created, and they vanish on refresh. Nothing is persisted and nothing is
   * read back.
   *
   * REPORTED, NOT FIXED, deliberately: the repair is a new
   * GET /surface-rentals/movements on the API, which is out of scope here.
   * Left in place rather than deleted so the wrong call is visible to whoever
   * builds it. See the PR body.
   */
  const { data: movementsRaw, isLoading: loadingMovements } = useQuery({
    queryKey: ['rentals-yard-movements'],
    queryFn: () => api.rentals.checkout.checklist().then(r => {
      const data = r.data as Movement[];
      return Array.isArray(data) ? data : [];
    }),
    staleTime: 30_000,
  });

  const movements = [...localMovements, ...(movementsRaw ?? [])];

  /**
   * ONE asset list, grouped by the statuses the API writes. It used to be four
   * calls (`available`, `deployed`, `maintenance`, `reserved`), two of them for
   * statuses the API never writes, settled through `allSettled`. A single read
   * also gives the page one honest failure state instead of four partial ones.
   */
  const assetsQuery = useQuery({
    queryKey: ['rentals-yard-assets'],
    queryFn: () => api.rentals.assets.list().then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toYardAsset)),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const assetsFailed = queryLoadFailure(assetsQuery) != null;
  const loadingAssets = assetsQuery.isLoading && !assetsFailed;
  const allAssets = assetsQuery.data;
  const group = (k: YardTab) => (allAssets ?? []).filter(YARD_CHIPS.find((c) => c.key === k)!.match);
  const availableAssets = group("available");
  const rentedAssets = group("rented");
  /** Tallies of the loaded list: GET /assets is unpaginated and no endpoint returns these. */
  const countOf = (k: YardTab): string | number => (assetsFailed || !allAssets ? "—" : displayValue(group(k).length));

  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const res = await api.rentals.orders.list();
      setOrders(unwrapArray<Record<string, unknown>>(res.data).map(toOrder));
    } catch {
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  // Fetch orders when checkout dialog opens
  useEffect(() => {
    if (checkoutDialogOpen) {
      void fetchOrders();
    }
  }, [checkoutDialogOpen, fetchOrders]);

  // ─── Derived data ─────────────────────────────────────────────────────────

  const filteredMovements = movements.filter((m) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      m.assetName.toLowerCase().includes(q) ||
      m.assetTag.toLowerCase().includes(q) ||
      m.customer.toLowerCase().includes(q);
    const matchesType = typeFilter === "all" || m.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const currentYardAssets: YardAsset[] = group(yardSubTab);

  const allChecklistConfirmed = checklistStatuses.every((s) => s !== "pending");
  const canSubmitCheckout = allChecklistConfirmed && checkoutAssetId && checkoutOrderId && !checkoutPhotosBusy;

  // ─── Checklist toggle ─────────────────────────────────────────────────────

  const setChecklistItem = (idx: number, status: ChecklistStatus) => {
    setChecklistStatuses((prev) => {
      const next = [...prev];
      next[idx] = status;
      return next;
    });
  };

  const resetCheckoutForm = () => {
    setChecklistStatuses(new Array(checklistItems.length).fill("pending") as ChecklistStatus[]);
    setCheckoutAssetId("");
    setCheckoutOrderId("");
    setCheckoutCondition("good");
    setCheckoutNotes("");
    setCheckoutInspectionId(newInspectionId());
    setCheckoutPhotos([]);
    setCheckoutPhotosBusy(false);
  };

  // ─── Check-Out submit ─────────────────────────────────────────────────────

  /**
   * Two writes, billing LAST, and nothing after it (#815).
   *
   *   1. POST /inspections, `pre_delivery`: where the condition, the notes and
   *      the checklist actually land. The periods POST has no column for any of
   *      them and used to drop all three. The Yard Movement report reads
   *      `pre_delivery` inspections as the "out" side, so this is also what
   *      puts the check-out in the log.
   *   2. POST /orders/:id/periods, `{ asset_id }` only. It starts billing and
   *      sets the asset to `rented` itself. The rate is resolved server-side
   *      and `on_rent_at` defaults to the server's now.
   *
   * There used to be a third write, PUT /assets/:id `{ status: "deployed" }`,
   * which overwrote that `rented`. The API counts only `rented` as on rent, so
   * every checked-out asset dropped out of the dashboard and billing counts.
   *
   * If (2) fails after (1), the inspection stays: there is no DELETE for it.
   * The error says so rather than claiming nothing happened.
   */
  const handleCheckoutSubmit = async () => {
    if (!canSubmitCheckout) return;
    setCheckoutSubmitting(true);

    const selectedAsset = availableAssets.find((a) => a.id === checkoutAssetId);
    const selectedOrder = orders.find((o) => o.id === checkoutOrderId);
    const assetLabel = selectedAsset?.name || "Equipment";

    try {
      await runWriteSteps([
        {
          label: "the pre-delivery inspection",
          run: () =>
            api.rentals.inspections.create({
              asset_id: checkoutAssetId,
              order_id: checkoutOrderId,
              inspection_type: "pre_delivery",
              condition: checkoutCondition,
              notes: checkoutNotes.trim() || undefined,
              inspector_name: currentUserName ?? undefined,
              checklist: checklistItems.map((item, idx) => ({ item, result: checklistStatuses[idx] })),
              ...inspectionPhotoExtras(checkoutInspectionId, checkoutPhotos),
            }),
        },
        {
          label: "the rental period that starts billing",
          run: () => api.rentals.orders.createPeriod(checkoutOrderId, { asset_id: checkoutAssetId }),
        },
      ]);

      // Add to movements locally for immediate feedback
      const newMovement: Movement = {
        id: `co-${Date.now()}`,
        type: "checkout",
        assetName: assetLabel,
        assetTag: selectedAsset?.tag || "",
        customer: selectedOrder?.customer || "",
        well: selectedOrder?.well || "",
        dateTime: formatInstant(new Date().toISOString(), timeZone, {
          month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
        }) ?? "\u2014",
        tech: currentUserName,
        condition: checkoutCondition,
        photos: checkoutPhotos.length,
      };
      setLocalMovements((prev) => [newMovement, ...prev]);

      setCheckoutDialogOpen(false);
      resetCheckoutForm();
      toast.success(`${assetLabel} checked out -- billing started`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Check-out failed");
    } finally {
      // Also after a failure: the inspection may have landed and changed the
      // asset's condition.
      queryClient.invalidateQueries({ queryKey: ['rentals-yard-assets'] });
      setCheckoutSubmitting(false);
    }
  };

  // ─── Check-In submit ──────────────────────────────────────────────────────

  /**
   * Every open rental period on this asset, from the API's own lookup
   * (`GET /periods?asset_id=…&open=true`, api#951). It used to list every
   * order and read each one's periods — one request per order. Open means the
   * API SAID null: the filter is the server's, and the `off_rent_at === null`
   * check stays so a row that is not open is never closed again.
   */
  const findOpenPeriods = async (assetId: string) => {
    const res = await api.rentals.periods.listForAsset(assetId, { open: true });
    return unwrapArray<RentalPeriodListRow>(res.data).filter((p) => p.asset_id === assetId && p.off_rent_at === null);
  };

  /**
   * Check-in used to PUT the asset back to `available` and do nothing else, so
   * the rental period stayed open and billing kept running after the asset was
   * home (#815). Now, in order:
   *
   *   1. POST /inspections, `post_return`, with the condition and notes. The
   *      Yard Movement report reads these as the "in" side.
   *   2. PATCH /periods/:id/close with `off_rent_at` = the instant Confirm was
   *      pressed. This stops billing and sets the asset to `available`.
   *   3. Only when the return is Damaged: PUT the asset to `maintenance`. It
   *      has to come after (2), because the close sets `available`.
   *
   * With no open period, nothing was billing: step 2 is skipped and step 3
   * sets the status either way. With MORE than one, this screen cannot tell
   * which to stop, so it writes nothing and says so.
   */
  const handleCheckinSubmit = async () => {
    if (!checkinAsset) return;
    setCheckinSubmitting(true);
    const asset = checkinAsset;
    const checkedInAt = new Date().toISOString();
    const damaged = checkinCondition === "damaged";

    try {
      let open: RentalPeriodListRow[];
      try {
        open = await findOpenPeriods(asset.id);
      } catch (err) {
        toast.error(`Could not look up ${asset.name}'s rental period: ${apiErrorText(err)}. Nothing was saved.`);
        return;
      }
      if (open.length > 1) {
        toast.error(
          `${asset.name} has ${open.length} open rental periods, so this screen cannot tell which one to stop. Nothing was saved.`,
        );
        return;
      }
      const period = open[0] ?? null;

      try {
        await runWriteSteps([
          {
            label: "the return inspection",
            run: () =>
              api.rentals.inspections.create({
                asset_id: asset.id,
                order_id: period?.order_id,
                inspection_type: "post_return",
                condition: checkinCondition,
                notes: checkinNotes.trim() || undefined,
                inspector_name: currentUserName ?? undefined,
                ...inspectionPhotoExtras(checkinInspectionId, checkinPhotos),
              }),
          },
          ...(period
            ? [{
                label: "the end of billing (rental period closed)",
                run: () => api.rentals.periods.close(period.id, { off_rent_at: checkedInAt }),
              }]
            : []),
          ...(damaged
            ? [{ label: "the move to Maintenance", run: () => api.rentals.assets.update(asset.id, { status: "maintenance" }) }]
            : period
              ? []
              : [{ label: "the move back to Available", run: () => api.rentals.assets.update(asset.id, { status: "available" }) }]),
        ]);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Check-in failed");
        return;
      }

      const newMovement: Movement = {
        id: `ci-${Date.now()}`,
        type: "return",
        assetName: asset.name,
        assetTag: asset.tag,
        customer: asset.current_order?.customer || "",
        well: asset.current_order?.well || "",
        dateTime: formatInstant(checkedInAt, timeZone, {
          month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
        }) ?? "\u2014",
        tech: currentUserName,
        condition: checkinCondition,
        photos: checkinPhotos.length,
      };
      setLocalMovements((prev) => [newMovement, ...prev]);

      setCheckinDialogOpen(false);
      const where = damaged ? "moved to Maintenance" : "moved to yard";
      toast.success(
        period
          ? `${asset.name} checked in -- billing stopped, ${where}`
          : `${asset.name} checked in -- ${where}. It had no open rental period, so nothing was billing.`,
      );
    } finally {
      queryClient.invalidateQueries({ queryKey: ['rentals-yard-assets'] });
      setCheckinSubmitting(false);
    }
  };

  // ─── Helpers to split dateTime for display ────────────────────────────────

  const splitDateTime = (dt: string) => {
    const parts = dt.split(/,?\s+/);
    // Try to separate date and time
    if (parts.length >= 4) {
      const timePart = parts[parts.length - 1];
      const datePart = parts.slice(0, parts.length - 1).join(" ");
      return { date: datePart, time: timePart };
    }
    return { date: dt, time: "" };
  };

  const tabClass = (active: boolean) =>
    `-mb-px border-b-2 px-1 pb-2 text-sm font-extrabold transition-colors ${
      active ? "border-fi-amber text-fieldiq-text" : "border-transparent text-fieldiq-muted hover:text-fieldiq-text"
    }`;

  // ─── Render ─────────────────────────────────────────────────────────────────

  const newCheckOut = (
    <button type="button" onClick={() => setCheckoutDialogOpen(true)} className="fi-action fi-action--standard fi-action--primary">
      <Plus className="size-4" aria-hidden="true" />
      New Check-Out
    </button>
  );

  const body = (
    <>
          {/* Tabs switch which question the page answers, so they carry no count. */}
          <div role="tablist" aria-label="Yard views" className="flex gap-5 border-b border-fieldiq-border">
            <button type="button" role="tab" aria-selected={activeTab === "yard"} onClick={() => setActiveTab("yard")} className={tabClass(activeTab === "yard")}>
              Yard Status
            </button>
            <button type="button" role="tab" aria-selected={activeTab === "pending"} onClick={() => setActiveTab("pending")} className={tabClass(activeTab === "pending")}>
              Check-In
            </button>
            <button type="button" role="tab" aria-selected={activeTab === "movements"} onClick={() => setActiveTab("movements")} className={tabClass(activeTab === "movements")}>
              This Session
            </button>
          </div>

          {/* ─── Yard Status ─────────────────────────────────────────────── */}
          {activeTab === "yard" && (
            <>
              <div className="scrollbar-hide flex gap-2 overflow-x-auto">
                {YARD_CHIPS.map((c) => {
                  const active = yardSubTab === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setYardSubTab(c.key)}
                      className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                        active
                          ? "bg-fi-amber text-fi-amber-ink"
                          : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                      }`}
                    >
                      {c.label}
                      {!loadingAssets && !assetsFailed && allAssets && (
                        <span className={`tabular-nums ${active ? "text-fi-amber-ink" : "text-fieldiq-muted"}`}>{group(c.key).length}</span>
                      )}
                    </button>
                  );
                })}
              </div>

              <section
                aria-labelledby="yard-register-title"
                className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
              >
                <header className="border-b border-fieldiq-border px-4 py-4">
                  <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Yard status</p>
                  <h2 id="yard-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                    Rental assets by status
                  </h2>
                </header>
                {assetsFailed ? (
                  <LoadFailureNotice error={queryFailureError(assetsQuery)} subject="rental assets" />
                ) : loadingAssets ? (
                  <div className="space-y-3 p-4" aria-hidden="true">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-10 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                    ))}
                  </div>
                ) : currentYardAssets.length === 0 ? (
                  <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">No assets with this status</p>
                ) : (
                  <>
                    <div className="hidden overflow-x-auto md:block">
                      <table className="w-full min-w-[720px] text-left">
                        <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                          <tr className="border-l-4 border-l-transparent text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                            <th className="px-4 py-3">Asset number</th>
                            <th className="px-4 py-3">Equipment</th>
                            <th className="px-4 py-3">Category</th>
                            <th className="px-4 py-3">Location</th>
                            <th className="px-4 py-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentYardAssets.map((asset) => (
                            <tr key={asset.id} data-testid="yard-row" className={`border-t border-l-4 border-fieldiq-border ${skinOf(asset.status).rail}`}>
                              <td className="px-4 py-3 font-mono text-sm font-bold text-fieldiq-text">{asset.tag || "—"}</td>
                              <td className="px-4 py-3 text-sm font-bold text-fieldiq-text">{asset.name}</td>
                              <td className="px-4 py-3 text-sm text-fieldiq-muted">{asset.category ?? "—"}</td>
                              <td className="px-4 py-3 text-sm text-fieldiq-muted">{asset.yard_location ?? "—"}</td>
                              <td className="px-4 py-3"><StatusBadge status={asset.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="space-y-3 p-4 md:hidden">
                      {currentYardAssets.map((asset) => (
                        <div key={asset.id} data-testid="yard-card" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate font-extrabold text-fieldiq-text">{asset.name}</p>
                              <p className="font-mono text-xs text-fieldiq-muted">{asset.tag || "—"}</p>
                            </div>
                            <StatusBadge status={asset.status} />
                          </div>
                          <p className="mt-2 text-sm text-fieldiq-text">
                            {asset.category ?? "—"} · {asset.yard_location ?? "—"}
                          </p>
                        </div>
                      ))}
                    </div>
                    {yardSubTab === "other" && (
                      <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                        These assets carry a status the API does not use, so this page cannot say whether they are in the yard or out.
                      </p>
                    )}
                  </>
                )}
              </section>
            </>
          )}

          {/* ─── Check-In ────────────────────────────────────────────────── */}
          {activeTab === "pending" && (
            <section
              aria-labelledby="checkin-title"
              className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
            >
              <header className="border-b border-fieldiq-border px-4 py-4">
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Check-in</p>
                <h2 id="checkin-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Assets on rent</h2>
              </header>
              {assetsFailed ? (
                <LoadFailureNotice error={queryFailureError(assetsQuery)} subject="assets on rent" />
              ) : loadingAssets ? (
                <div className="space-y-3 p-4" aria-hidden="true">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-14 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                  ))}
                </div>
              ) : rentedAssets.length === 0 ? (
                <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">No assets are on rent</p>
              ) : (
                <div className="divide-y divide-fieldiq-border">
                  {rentedAssets.map((item) => (
                    <div key={item.id} data-testid="checkin-row" className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-fi-control bg-fieldiq-dark">
                          <RotateCcw className="h-5 w-5 text-fieldiq-muted" aria-hidden="true" />
                        </div>
                        <div>
                          <p className="font-bold text-fieldiq-text">{item.name}</p>
                          <p className="font-mono text-xs text-fieldiq-muted">{item.tag || "—"}</p>
                          <p className="text-xs text-fieldiq-muted">{item.yard_location ?? "—"}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          setCheckinAsset(item);
                          setCheckinCondition("good");
                          setCheckinNotes("");
                          setCheckinInspectionId(newInspectionId());
                          setCheckinPhotos([]);
                          setCheckinPhotosBusy(false);
                          setCheckinDialogOpen(true);
                        }}
                      >
                        Check In Equipment
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ─── This Session ────────────────────────────────────────────── */}
          {activeTab === "movements" && (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1 sm:max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Search equipment, customer..."
                    aria-label="Search movements"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
                  />
                </div>
                <select
                  aria-label="Filter by movement type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none"
                >
                  <option value="all">All movements</option>
                  <option value="checkout">Check-outs</option>
                  <option value="return">Returns</option>
                </select>
              </div>

              <section
                aria-labelledby="movements-title"
                className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
              >
                <header className="border-b border-fieldiq-border px-4 py-4">
                  <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">This session</p>
                  <h2 id="movements-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                    Check-outs and check-ins made here
                  </h2>
                </header>
                {loadingMovements ? (
                  <div className="h-24 animate-pulse bg-fieldiq-dark/40" aria-hidden="true" />
                ) : filteredMovements.length === 0 ? (
                  <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">No check-outs or check-ins in this session</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                        <tr className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                          <th className="px-4 py-3">Type</th>
                          <th className="px-4 py-3">Equipment</th>
                          <th className="px-4 py-3">Customer / Site</th>
                          <th className="px-4 py-3">Date &amp; Time</th>
                          <th className="px-4 py-3">Tech</th>
                          <th className="px-4 py-3">Condition</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMovements.map((movement) => {
                          const { date, time } = splitDateTime(movement.dateTime);
                          return (
                            <tr key={movement.id} className="border-t border-fieldiq-border">
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1 rounded-fi-pill border border-fieldiq-border px-2 py-0.5 text-xs font-bold text-fieldiq-text">
                                  {movement.type === "checkout" ? <ArrowUpRight className="h-3 w-3" aria-hidden="true" /> : <ArrowDownLeft className="h-3 w-3" aria-hidden="true" />}
                                  {movement.type === "checkout" ? "Out" : "In"}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm font-bold text-fieldiq-text">{movement.assetName}</p>
                                <p className="font-mono text-xs text-fieldiq-muted">{movement.assetTag || "—"}</p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-fieldiq-text">{movement.customer || "—"}</p>
                                <p className="text-xs text-fieldiq-muted">{movement.well || "—"}</p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-fieldiq-text">{date}</p>
                                <p className="text-xs text-fieldiq-muted">{time}</p>
                              </td>
                              <td className="px-4 py-3 text-sm text-fieldiq-text">{movement.tech ?? "—"}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none text-fi-signal-ink ${conditionConfig[movement.condition].fill}`}>
                                  {conditionConfig[movement.condition].label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Scope: this list is local to the browser tab and gone on refresh. */}
                <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                  Only movements made in this browser session, and they clear on refresh.
                  {/* The report is an office route; the field tree has no way to it. */}
                  {!isField && (
                    <>
                      {" "}The full log is the{" "}
                      <Link to="/reports/yard-movement" className="font-bold text-fieldiq-text underline">Yard Movement report</Link>.
                    </>
                  )}
                </p>
              </section>
            </>
          )}
    </>
  );

  return (
    <>
    {isField ? (
      <FieldPageChrome
        testId="rental-yard-page"
        title="Yard check-out/in"
        onBack={() => navigate("/my-work")}
        action={newCheckOut}
      >
        {body}
      </FieldPageChrome>
    ) : (
      <div data-testid="rental-yard-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals"
        title="Yard / Check-Out"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={newCheckOut}
        stats={[
          { icon: <PackageCheck className="size-4" aria-hidden="true" />, label: "Available", sub: "In the yard", tone: "success", loading: loadingAssets, value: countOf("available") },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: "On Rent", sub: "Out on order", tone: "info", loading: loadingAssets, value: countOf("rented") },
          { icon: <HelpCircle className="size-4" aria-hidden="true" />, label: "Unknown", sub: "Status unread", tone: "neutral", loading: loadingAssets, value: countOf("other") },
          { icon: <Wrench className="size-4" aria-hidden="true" />, label: "Maintenance", sub: "In the shop", tone: "accent", loading: loadingAssets, value: countOf("maintenance") },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">{body}</div>
      </section>
      </div>
    )}

      {/* ─── Check-Out Dialog ───────────────────────────────────────────── */}
      <Dialog open={checkoutDialogOpen} onOpenChange={(open) => { setCheckoutDialogOpen(open); if (!open) resetCheckoutForm(); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Equipment Check-Out</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <h3 className="mb-3 font-semibold">Logistics</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Native and labelled: ui/select prints the raw value (an id) in its trigger. */}
                <div>
                  <Label htmlFor="co-asset">Equipment</Label>
                  <select id="co-asset" value={checkoutAssetId} onChange={(e) => setCheckoutAssetId(e.target.value)} className={nativeSelect}>
                    <option value="">{availableAssets.length === 0 ? "No available assets" : "Select asset..."}</option>
                    {availableAssets.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.tag ? `${a.tag} — ${a.name}` : a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="co-order">Order</Label>
                  <select id="co-order" value={checkoutOrderId} onChange={(e) => setCheckoutOrderId(e.target.value)} className={nativeSelect}>
                    <option value="">{loadingOrders ? "Loading..." : "Select order..."}</option>
                    {orders.map((o) => (
                      <option key={o.id} value={o.id}>
                        {[o.order_number, o.customer || null, o.well].filter(Boolean).join(" — ") || "—"}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="co-condition">Condition</Label>
                  <select id="co-condition" value={checkoutCondition} onChange={(e) => setCheckoutCondition(e.target.value as "good" | "fair" | "damaged")} className={nativeSelect}>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="damaged">Damaged</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="border-t border-fieldiq-border pt-6">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
                Pre-Departure Checklist
                <span className="ml-auto text-xs font-normal text-fieldiq-muted">
                  {checklistStatuses.filter((s) => s !== "pending").length}/{checklistItems.length}
                </span>
              </h3>
              <div className="space-y-2">
                {checklistItems.map((item, idx) => {
                  const status = checklistStatuses[idx];
                  return (
                    <div
                      key={idx}
                      className={`flex w-full items-center gap-3 rounded-fi-control border p-3 transition-colors ${
                        status === "ok" ? "border-fi-green" : status === "issue" ? "border-fi-amber" : "border-fieldiq-border"
                      }`}
                    >
                      <span className="flex-1 text-sm">{item}</span>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          aria-pressed={status === "ok"}
                          onClick={() => setChecklistItem(idx, status === "ok" ? "pending" : "ok")}
                          className={`inline-flex h-7 items-center rounded-fi-control border px-2.5 text-xs font-bold ${
                            status === "ok" ? "border-black/20 bg-fi-signal-green text-fi-signal-ink" : "border-fieldiq-border text-fieldiq-text hover:border-fi-green"
                          }`}
                        >
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                          OK
                        </button>
                        <button
                          type="button"
                          aria-pressed={status === "issue"}
                          onClick={() => setChecklistItem(idx, status === "issue" ? "pending" : "issue")}
                          className={`inline-flex h-7 items-center rounded-fi-control border px-2.5 text-xs font-bold ${
                            status === "issue" ? "border-black/20 bg-fi-signal-amber text-fi-signal-ink" : "border-fieldiq-border text-fieldiq-text hover:border-fi-amber"
                          }`}
                        >
                          <AlertTriangle className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                          Issue
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Photos go on the pre-delivery inspection this check-out writes,
                as confirmed uploads against its id (api#1066). Optional: the
                4-side boxes that used to be required uploaded nothing (#815). */}
            <div className="border-t border-fieldiq-border pt-6">
              <RentalInspectionPhotoField
                key={checkoutInspectionId}
                inspectionId={checkoutInspectionId}
                idPrefix="yard-checkout"
                onChange={setCheckoutPhotos}
                onBusyChange={setCheckoutPhotosBusy}
              />
            </div>

            <div className="border-t border-fieldiq-border pt-6">
              <Label>Notes</Label>
              <Textarea
                placeholder="Any additional notes about the equipment condition..."
                rows={3}
                value={checkoutNotes}
                onChange={(e) => setCheckoutNotes(e.target.value)}
              />
            </div>

            <Button
              className="w-full"
              size="lg"
              disabled={!canSubmitCheckout || checkoutSubmitting}
              onClick={() => void handleCheckoutSubmit()}
            >
              {checkoutSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Truck className="mr-2 h-4 w-4" />}
              {checkoutSubmitting ? "Processing..." : "Complete Check-Out & Start Billing"}
            </Button>
            {!canSubmitCheckout && !checkoutSubmitting && (
              <p className="text-center text-xs text-fieldiq-muted">
                Select asset &amp; order and confirm all checklist items (OK/Issue) to proceed
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Check-In Dialog ────────────────────────────────────────────── */}
      <Dialog open={checkinDialogOpen} onOpenChange={setCheckinDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Check In Equipment</DialogTitle>
          </DialogHeader>
          {checkinAsset && (
            <div className="space-y-4 py-2">
              <div className="rounded-fi-control bg-fieldiq-dark/50 p-3">
                <p className="font-bold">{checkinAsset.name}</p>
                <p className="font-mono text-xs text-fieldiq-muted">{checkinAsset.tag || "—"}</p>
              </div>
              <div>
                <Label htmlFor="ci-condition">Return Condition</Label>
                <select id="ci-condition" value={checkinCondition} onChange={(e) => setCheckinCondition(e.target.value as "good" | "fair" | "damaged")} className={nativeSelect}>
                  <option value="good">Good -- Ready to redeploy</option>
                  <option value="fair">Fair -- Needs cleaning/minor repair</option>
                  <option value="damaged">Damaged -- Needs major repair</option>
                </select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  placeholder="Describe any damage, wear, or notes..."
                  value={checkinNotes}
                  onChange={(e) => setCheckinNotes(e.target.value)}
                  rows={3}
                />
              </div>
              <RentalInspectionPhotoField
                key={checkinInspectionId}
                inspectionId={checkinInspectionId}
                idPrefix="yard-checkin"
                onChange={setCheckinPhotos}
                onBusyChange={setCheckinPhotosBusy}
              />
              <Button className="w-full" disabled={checkinSubmitting || checkinPhotosBusy} onClick={() => void handleCheckinSubmit()}>
                {checkinSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                {checkinSubmitting ? "Processing..." : "Confirm Check-In"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
